import { constants, useBreakpointRange, useKeyUp } from "@newsela/angelou";
import { useEffect, useRef, useState } from "react";
import FocusLock from "react-focus-lock";
import { FocusOn } from "react-focus-on";

import { BannerMode } from "@/types/BannerMode.interface";
import { ArticleLevel } from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import ActivityContainer from "@/view/ActivityContainer";
import contentTypes from "@/view/constants/contentTypes";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import quizOmissionReasons from "@/view/constants/quizOmissionReasons";
import videoPlayerEvents from "@/view/constants/videoPlayerEvents";
import { Metadata } from "@/view/ContentHead";
import useActivityPanel from "@/view/utils/useActivityPanel";
import useAnnotationsState from "@/view/utils/useAnnotationsState";
import useFocusManager from "@/view/utils/useFocusManager";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import useSetupVideoEventListener from "@/view/utils/useSetupVideoEventListener";
import useVideoEventBus from "@/view/utils/useVideoEventBus";
import {
  containerCss,
  focusContainerCss,
  panelCss,
  wrapperCss,
} from "./ActivityPanel.style";
import ActivityPanelTab from "./ActivityPanelTab";

const { zeroToXs, mdToLg, lgToInfinity } =
  constants.breakpoints.BreakpointRanges;

interface ActivityPanelProps {
  assignments: Assignment[];
  assignmentsLoading: boolean;
  assignModalTriggerRef: React.Ref<HTMLButtonElement> | null;
  bannerMode: BannerMode;
  content: {
    content_type: keyof typeof contentTypes;
    data: {
      is_lite?: boolean;
      article_levels: ArticleLevel[];
      quiz_omission_reason: keyof typeof quizOmissionReasons | null;
    };
    id: string;
    is_lite: boolean;
  };
  contentId: string;
  contentMetadata: Metadata[];
  contentType: typeof contentTypes;
  currentAssignment: Assignment;
  currentAssignmentId: string;
  currentLevel: ArticleLevel;
  isBannerModeActive: boolean;
  isLevelLocked: boolean;
  isSelectedAssignmentLoading: boolean;
  fetchAssignment: (arg: string) => void;
  latestAssignment: Assignment | undefined;
  legacyArticleLevelData: object;
  assignModalProps: object;
  quizOmissionReason: typeof quizOmissionReasons;
  selectedAssignment: Assignment;
  studentId: number;
  toggleVocabularySupport: (a: boolean) => void;
  mobileActionsToolbarDropdownRef: React.Ref<HTMLButtonElement> | null;
  unlockLevel: () => void;
  user: {
    id: number | string;
    unique_id: string;
  };
  userProperties: UserProperties;
}

const ActivityPanel = ({
  assignments,
  assignmentsLoading,
  content,
  contentMetadata,
  contentId,
  contentType,
  currentLevel,
  isBannerModeActive,
  isSelectedAssignmentLoading,
  fetchAssignment,
  latestAssignment,
  legacyArticleLevelData,
  quizOmissionReason,
  selectedAssignment,
  studentId,
  unlockLevel,
  user,
  userProperties,
}: ActivityPanelProps) => {
  const { isActivityPanelExpanded, setIsActivityPanelExpanded } =
    useActivityPanel();
  const activityPanelTabRef = useRef<HTMLButtonElement>(null);
  const previousIsActivityPanelExpanded = useRef(false);
  const wasOpenedByUserRef = useRef(false);
  const shouldFocusCloseButtonRef = useRef(false);
  const [inProgressQuizzes, updateinProgressQuizzes] = useState({});
  const [inProgressPowerWords, updateinProgressPowerWords] = useState({});
  const [activeVideoActivityLabel, setActiveVideoActivityLabel] = useState("");
  const { draftAnnotation } = useAnnotationsState();
  const { isVideoPresentModeActive, isPresentModeActive } =
    usePresentModeContext();
  const { VIEW_PAGE_EVENT, ANNOTATION_EVENT } = videoPlayerEvents;
  const { sendVideoPlayerEvent } = useVideoEventBus();

  const breakpointRange = useBreakpointRange();
  const isSmallerScreen =
    breakpointRange !== mdToLg && breakpointRange !== lgToInfinity;
  const isSmallestScreen = breakpointRange === zeroToXs;
  const { isStudent } = userProperties;

  const onEsc = () => {
    if (isSmallerScreen) {
      setIsActivityPanelExpanded(false);
    }
  };

  useKeyUp("escape", onEsc, {
    dependencyArray: [breakpointRange],
  });

  const { focusElement } = useFocusManager();

  const handleActivateEvent = (e: MouseEvent) => {
    if (e.type === "quiz") {
      focusElement(focusElementRefKeys.activityPanelBackButton);
      setActiveVideoActivityLabel("Quiz question");
    }
  };

  useSetupVideoEventListener(VIEW_PAGE_EVENT.RESET_ACTIVITY_PANEL_CONTENTS);
  useSetupVideoEventListener(ANNOTATION_EVENT.ACTIVATE, handleActivateEvent);

  useEffect(() => {
    if (draftAnnotation) {
      setIsActivityPanelExpanded(true);
    }
  }, [JSON.stringify(draftAnnotation), isActivityPanelExpanded]);

  useEffect(() => {
    const panelJustOpened =
      isActivityPanelExpanded && !previousIsActivityPanelExpanded.current;
    const panelJustClosed =
      !isActivityPanelExpanded && previousIsActivityPanelExpanded.current;

    if (panelJustOpened && wasOpenedByUserRef.current) {
      // Signal the PanelHeader to focus the close button when panel opens via user click
      shouldFocusCloseButtonRef.current = true;
    }

    if (panelJustClosed) {
      wasOpenedByUserRef.current = false;
      // Focus the ActivityPanelTab when panel closes
      requestAnimationFrame(() => {
        activityPanelTabRef.current?.focus();
      });
    }

    previousIsActivityPanelExpanded.current = isActivityPanelExpanded;
  }, [isActivityPanelExpanded]);

  const handleActivityTabClick = () => {
    wasOpenedByUserRef.current = true;
    setIsActivityPanelExpanded(true);
    sendVideoPlayerEvent(VIEW_PAGE_EVENT.RESET_ACTIVITY_PANEL_CONTENTS);
  };

  const activityPanelContents = (
    <ActivityContainer
      activeVideoActivityLabel={activeVideoActivityLabel}
      assignments={assignments}
      assignmentsLoading={assignmentsLoading}
      content={content}
      contentMetadata={contentMetadata}
      contentId={contentId}
      contentType={contentType}
      currentLevel={currentLevel}
      fetchAssignment={fetchAssignment}
      inProgressPowerWords={inProgressPowerWords}
      inProgressQuizzes={inProgressQuizzes}
      isBannerModeActive={isBannerModeActive}
      isPresentModeActive={isPresentModeActive}
      isSelectedAssignmentLoading={isSelectedAssignmentLoading}
      latestAssignment={latestAssignment}
      legacyArticleLevelData={legacyArticleLevelData}
      quizOmissionReason={quizOmissionReason}
      selectedAssignment={selectedAssignment}
      setActiveVideoActivityLabel={setActiveVideoActivityLabel}
      shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      unlockLevel={unlockLevel}
      updateinProgressPowerWords={updateinProgressPowerWords}
      updateinProgressQuizzes={updateinProgressQuizzes}
      user={user}
      studentId={studentId}
      userProperties={userProperties}
    />
  );

  const getContentsWithFocusLockComponent = (isPanelVisible: boolean) => {
    // FocusOn prevents screen readers from reading hidden elements within in the activities panel when view on mobile devices.
    if (isSmallestScreen) {
      // There is a bug in FocusOn that prevents click events from being registered
      // when the panel is hidden. So we wrap panel content in FocusOn only when
      // the panel is opened, and we actually need trap the focus within the panel
      return isPanelVisible ? (
        <FocusOn scrollLock={false} css={focusContainerCss}>
          {activityPanelContents}
        </FocusOn>
      ) : (
        activityPanelContents
      );
    }
    // FocusLock maintains focus in the panel, but allows scrolling on the page when small/medium screens are used.
    return (
      <FocusLock
        disabled={!isSmallerScreen}
        returnFocus
        css={focusContainerCss}
      >
        <div>{activityPanelContents}</div>
      </FocusLock>
    );
  };

  return (
    <div data-qa-selector="activity-panel" css={wrapperCss}>
      <div>
        {isActivityPanelExpanded ? null : (
          <ActivityPanelTab
            isStudent={isStudent}
            onClick={handleActivityTabClick}
            ref={activityPanelTabRef}
          />
        )}
      </div>
      <div css={panelCss(isActivityPanelExpanded, isVideoPresentModeActive)}>
        <div css={containerCss} role="dialog" aria-label="Activity panel">
          {getContentsWithFocusLockComponent(isActivityPanelExpanded)}
        </div>
      </div>
    </div>
  );
};

export default ActivityPanel;
